/**
 * Helpers para detecção e formatação de setores/blocos.
 *
 * Regra de bloco: o nome do setor casa com `^([A-Za-zÀ-ÿ]+)\s*(\d+)$`
 * Exemplos: A1 → bloco "A", C4 → bloco "C", Laranja → individual (sem bloco)
 */

// ─── Detecção ─────────────────────────────────────────────────────────────────

/**
 * Retorna o prefixo do bloco (maiúsculo) ou null se for setor individual.
 * Ex: "A1" → "A", "C4" → "C", "Laranja" → null
 */
export function blocoDoSetor(nome: string): string | null {
  const m = nome.trim().match(/^([A-Za-zÀ-ÿ]+)\s*(\d+)$/);
  return m ? m[1].toUpperCase() : null;
}

// ─── Agrupamento ──────────────────────────────────────────────────────────────

export interface SetorInfo { id: string; nome: string }

/**
 * Agrupa todos os setores em blocos + lista de individuais.
 * Cada bloco é ordenado numericamente pelo nome.
 */
export function agruparSetores(allSetores: SetorInfo[]): {
  blocos: Record<string, SetorInfo[]>;
  individuais: SetorInfo[];
} {
  const blocos: Record<string, SetorInfo[]> = {};
  const individuais: SetorInfo[] = [];

  for (const s of allSetores) {
    const bloco = blocoDoSetor(s.nome);
    if (bloco) {
      if (!blocos[bloco]) blocos[bloco] = [];
      blocos[bloco].push(s);
    } else {
      individuais.push(s);
    }
  }

  // Ordenar cada bloco numericamente (A1 < A2 < A10)
  for (const b of Object.keys(blocos)) {
    blocos[b].sort((a, z) => a.nome.localeCompare(z.nome, "pt-BR", { numeric: true }));
  }

  return { blocos, individuais };
}

// ─── Formatação ───────────────────────────────────────────────────────────────

/**
 * Monta o rótulo colapsado para exibição:
 *   - Bloco inteiro selecionado → "Bloco C"
 *   - Subconjunto do bloco → "C1, C2"
 *   - Individuais → pelo nome
 *   - Mix → "Bloco C, Laranja"
 */
export function formatSetores(selectedIds: string[], allSetores: SetorInfo[]): string {
  if (selectedIds.length === 0) return "";
  const selectedSet = new Set(selectedIds);
  const { blocos, individuais } = agruparSetores(allSetores);
  const parts: string[] = [];

  // Processar blocos em ordem alfabética
  for (const bloco of Object.keys(blocos).sort()) {
    const setoresBloco = blocos[bloco];
    const inBloco = setoresBloco.filter(s => selectedSet.has(s.id));
    if (inBloco.length === 0) continue;
    if (inBloco.length === setoresBloco.length) {
      parts.push(`Bloco ${bloco}`);
    } else {
      for (const s of inBloco) parts.push(s.nome);
    }
  }

  // Individuais selecionados
  for (const s of individuais) {
    if (selectedSet.has(s.id)) parts.push(s.nome);
  }

  return parts.join(", ");
}
